import React, { useEffect, useState } from 'react';
import { Table, Input, Row, Space } from 'antd';
import { ColumnGroupType, ColumnType } from 'antd/es/table';
import PrimaryButton from '../../../components/buttons/PrimaryButton';
import { useTranslation } from 'react-i18next';
import SecondaryButton from '../../../components/buttons/SecondaryButton';
import DefaultButton from '../../../components/buttons/DefaultButton';
import { FUEL_TYPE, FUEL_TYPES as Fuel_Type } from '../../../enums/FuelDispenserVerificationEnum';

type RowType = {
  key: number;
  fuelType: string;
  fuelTypeLabel: string;
};

type RowState = {
  priceFound?: string;
  pumpsTestedValues?: Record<number, string[]>;
};

interface TableProps {
  submit?: (data: Record<string, RowState>) => void;
  initialValues?: Record<string, RowState>;
  isViewMode?: boolean;
  numPumps?: number;
  handleBack?: () => void;
  handleCancel: () => void;
  onValidationError?: (message: string) => void;
}

const FUEL_TYPES: Array<{ key: FUEL_TYPE; label: string }> = Fuel_Type as {
  key: FUEL_TYPE;
  label: string;
}[];

const FuelDispenserVerificationTable: React.FC<TableProps> = ({
  submit,
  initialValues = {},
  isViewMode = false,
  numPumps = 16,
  handleBack,
  handleCancel,
  onValidationError,
}) => {
  const [rowValues, setRowValues] = useState<Record<string, RowState>>(
    initialValues && initialValues
  );
  const { t } = useTranslation('price');

  useEffect(() => {
    if (initialValues && Object.keys(initialValues).length > 0) {
      setRowValues(initialValues);
    }
  }, [initialValues]);

  const validateTableData = (): boolean => {
    // Check if at least one fuel type has a price found value
    const filledFuelTypes = Object.keys(rowValues).filter(
      (fuelType) => rowValues[fuelType]?.priceFound && rowValues[fuelType]?.priceFound !== ''
    );

    if (filledFuelTypes.length === 0) {
      onValidationError?.(t('atLeastOneFuelTypeRequired'));
      return false;
    }

    // For each filled fuel type, check if at least one pump has at least one value
    for (const fuelType of filledFuelTypes) {
      const pumpsTestedValues = rowValues[fuelType]?.pumpsTestedValues || {};
      const hasAtLeastOneValue = Object.values(pumpsTestedValues).some((pumpReadings) =>
        pumpReadings.some((reading) => reading && reading !== '')
      );

      if (!hasAtLeastOneValue) {
        onValidationError?.(t('atLeastOnePumpValueRequired'));
        return false;
      }
    }

    return true;
  };

  const rows: RowType[] = FUEL_TYPES.map((fuel, index) => ({
    key: index + 1,
    fuelType: fuel.key,
    fuelTypeLabel: t(fuel.label),
  }));

  const validateDecimalInput = (value: string): string => {
    if (!value) return '';

    const numericValue = value.replace(/[^\d.]/g, '');

    const parts = numericValue.split('.');
    if (parts.length > 2) return rowValues[parts[0]]?.priceFound || '';

    if (parts.length === 2) {
      return `${parts[0]}.${parts[1].slice(0, 2)}`;
    }

    return parts[0];
  };

  const updateRow = (fuelType: string, updates: Partial<RowState>) => {
    setRowValues((prev) => ({
      ...prev,
      [fuelType]: {
        ...prev[fuelType],
        ...updates,
      },
    }));
  };

  const updatePumpValue = (
    fuelType: string,
    pumpNumber: number,
    readingIndex: number,
    value: string
  ) => {
    const validatedValue = validateDecimalInput(value);
    setRowValues((prev) => ({
      ...prev,
      [fuelType]: {
        ...prev[fuelType],
        pumpsTestedValues: {
          ...(prev[fuelType]?.pumpsTestedValues || {}),
          [pumpNumber]: [...(prev[fuelType]?.pumpsTestedValues?.[pumpNumber] || ['', '', ''])].map(
            (val, idx) => (idx === readingIndex ? validatedValue : val)
          ),
        },
      },
    }));
  };

  const pumpColumns: ColumnType<RowType>[] = Array.from({ length: numPumps }, (_, i) => ({
    title: `${i + 1}`,
    key: `pump_${i + 1}`,
    width: 100,
    align: 'center' as const,
    render: (_: any, record: RowType) => {
      const pumpValues = rowValues[record.fuelType]?.pumpsTestedValues?.[i + 1] || ['', '', ''];
      return (
        <Space direction="vertical" style={{ width: '100%' }} size={0}>
          {[0, 1, 2].map((readingIdx) => (
            <div
              key={`pump_${i + 1}_reading_${readingIdx}`}
              style={{
                borderBottom: readingIdx < 2 ? '1px solid #d9d9d9' : 'none',
                padding: '4px 0',
              }}
            >
              <Input
                variant="borderless"
                className="m-0 p-0 text-center text-sm"
                value={pumpValues[readingIdx] || ''}
                onChange={(e) => {
                  if (String(e.target.value) == '0') return;
                  updatePumpValue(record.fuelType, i + 1, readingIdx, e.target.value);
                }}
                onBlur={() => {
                  if (pumpValues[readingIdx] !== undefined && pumpValues[readingIdx] !== '') {
                    const formattedValue = Number(pumpValues[readingIdx]).toFixed(2);
                    const currentPumpValues = [...pumpValues];
                    currentPumpValues[readingIdx] = formattedValue;
                    setRowValues((prev) => ({
                      ...prev,
                      [record.fuelType]: {
                        ...prev[record.fuelType],
                        pumpsTestedValues: {
                          ...(prev[record.fuelType]?.pumpsTestedValues || {}),
                          [i + 1]: currentPumpValues,
                        },
                      },
                    }));
                  }
                }}
                placeholder="0.00"
                disabled={isViewMode}
                maxLength={8}
              />
            </div>
          ))}
        </Space>
      );
    },
  }));

  const columns: (ColumnType<RowType> | ColumnGroupType<RowType>)[] = [
    {
      title: t('fuelTypeTitle'),
      dataIndex: 'fuelTypeLabel',
      width: 220,
      align: 'left' as const,
    },
    {
      title: t('priceFoundTitle'),
      width: 150,
      align: 'center' as const,
      render: (_: any, record: RowType) => {
        const rawValue = rowValues[record.fuelType]?.priceFound;
        return (
          <Space.Compact>
            <Input
              variant="borderless"
              className="m-0 p-0 text-center"
              value={rawValue ?? ''}
              onChange={(e) => {
                if (String(e.target.value) == '0') return;
                const validatedValue = validateDecimalInput(e.target.value);
                updateRow(record.fuelType, { priceFound: validatedValue });
              }}
              onBlur={() => {
                if (rawValue !== undefined && rawValue !== '') {
                  updateRow(record.fuelType, {
                    priceFound: Number(rawValue).toFixed(2),
                  });
                }
              }}
            />
            <div className="flex items-center justify-center">
              <p className="text-center text-gray-400">HNL</p>
            </div>
          </Space.Compact>
        );
      },
    },
    {
      title: t('pumpsTestedTitle'),
      children: pumpColumns,
    },
  ];

  return (
    <>
      <Table
        rowKey="fuelType"
        dataSource={rows}
        columns={columns}
        pagination={false}
        bordered
        scroll={{ x: 'max-content' }}
      />
      {!isViewMode && (
        <div className="mt-6 flex w-full flex-col justify-end gap-4 pt-4 md:ml-auto md:w-1/2 md:flex-row">
          <DefaultButton handleOnClick={handleCancel} width="100%">
            {t('cancel')}
          </DefaultButton>
          <SecondaryButton
            handleOnClick={() => {
              handleBack?.();
            }}
            width="100%"
          >
            {t('goBack')}
          </SecondaryButton>
          <PrimaryButton
            handleOnClick={() => {
              if (validateTableData()) {
                submit ? submit(rowValues) : () => {};
              }
            }}
            width="100%"
            hideBgGradient={true}
          >
            {t('goNext')}
          </PrimaryButton>
        </div>
      )}
    </>
  );
};

export default FuelDispenserVerificationTable;
